import { NavSubLevelModal } from "@NA/Services/ADIShopMyBranchService";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { IconProps } from "@insite/mobius/Icon";
import Link, { StyledIcon } from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";
import Img from "@insite/mobius/Img";
import { ResolveThunks, connect } from "react-redux";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";

interface OwnProps {
    title?: string;
    leftNavData?: NavSubLevelModal[];
    showMoreButton?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const productsCategory = getCategoriesDataView(state).value?.filter(p => p?.name === "bycat")[0];
    const productSubCategories = productsCategory?.subCategoryIds?.map(item => getCategoryState(state, item).value);
    const session = getSession(state);
    const brandsData = state.data.brands.byId;

    return {
        productsCategory,
        productSubCategories,
        session,
        brandsData,
    };
};

const mapDispatchToProps = {
    loadBrands,
    loadAllBrands,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ADILeftNav = ({
    title,
    leftNavData,
    showMoreButton,
    productSubCategories,
    loadAllBrands,
    session,
    brandsData,
}: Props) => {
    interface ShopMyBranchDCLeftNavStyles {
        listStyle?: InjectableCss;
        innerUnorderedListStyle?: InjectableCss;
        innerListItemStyle?: InjectableCss;
        moreBrandsLinkStyle?: InjectableCss;
        menuItemIcon?: IconProps;
    }

    const shopMyBranchDCLeftNavStyles: ShopMyBranchDCLeftNavStyles = {
        listStyle: {
            css: css`
                > ul {
                    list-style: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    max-height: 100%;
                    width: 100% !important;
                    padding-right: 22px !important;
                    overflow: auto !important;
                    &::-webkit-scrollbar {
                        width: 5px;
                    }
                    &::-webkit-scrollbar-thumb {
                        background: ${ADIColors.gray7};
                        border-radius: 10px;
                    }
                }
                .parent-menu {
                    display: flex;
                    justify-content: space-between;
                    padding-bottom: 10px;
                    padding-top: 5px;
                    line-height: 1.5;
                    svg {
                        height: auto;
                        width: 14px;
                    }
                }
            `,
        },
        innerUnorderedListStyle: {
            css: css`
                max-height: 100%;
                width: 100% !important;
                padding-right: 22px !important;
                overflow: auto !important;
                &::-webkit-scrollbar {
                    width: 5px;
                }
                &::-webkit-scrollbar-thumb {
                    background: ${ADIColors.gray7};
                    border-radius: 10px;
                }
            `,
        },
        innerListItemStyle: {
            css: css`
                margin-bottom: 12px;
                > a {
                    font-size: 14px;
                    font-weight: normal;
                    line-height: normal;
                    letter-spacing: normal;
                    &:hover {
                        text-decoration: underline;
                    }
                    > span {
                        color: ${ADIColors.black} !important;
                    }
                }
            `,
        },
        moreBrandsLinkStyle: {
            css: css`
                margin-bottom: 12px;
                font-weight: bold !important;
                color: ${ADIColors.blue5} !important;
                > a {
                    &:hover {
                        text-decoration: underline;
                    }
                    img {
                        width: 13px;
                        height: 15px;
                        object-fit: contain;
                        margin-right: 4.6px;
                        vertical-align: baseline;
                        top: 2px;
                        position: relative;
                    }
                }
            `,
        },
        menuItemIcon: {
            css: css`
                margin: 0 0 0 10px;
            `,
            size: 18,
            color: `${ADIColors.black}`,
        },
    };

    const [expandNav, setExpanNav] = useState<boolean>(true);

    useEffect(() => {
        loadAllBrands({
            sort: "name asc",
            select: "id,detailPagePath,name",
        });
    }, []);
    
    const byIdArray = Object.values(brandsData);

    byIdArray.sort((a, b) => a.name.localeCompare(b.name));
    
    const showListOptionStyle = {
        css: css`
            display: ${expandNav ? "block" : "none"};
        `,
    };

    const downIcon = (
        <StyledIcon
            src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/filter-down.svg"
            {...shopMyBranchDCLeftNavStyles.menuItemIcon}
        />
    );
    const upIcon = (
        <StyledIcon
            src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/newicons/filter-up.svg"
            {...shopMyBranchDCLeftNavStyles.menuItemIcon}
        />
    );

    return (
        <ListItem {...shopMyBranchDCLeftNavStyles.listStyle}>
            <StyledWrapper onClick={() => setExpanNav(!expandNav)} className="parent-menu">
                <StyledWrapper>{translate(`${title}`)}</StyledWrapper>
                {expandNav ? upIcon : downIcon}
            </StyledWrapper>
            <UnorderedList {...shopMyBranchDCLeftNavStyles.innerUnorderedListStyle} {...showListOptionStyle}>
                {session.isAuthenticated && leftNavData?.map((eachOption: NavSubLevelModal) => (
                    <>
                        <ListItem {...shopMyBranchDCLeftNavStyles.innerListItemStyle} key={eachOption.name}>
                            <Link href={eachOption.path}>{eachOption.name}</Link>
                        </ListItem>
                    </>
                ))}
                {!session.isAuthenticated &&
                    title === "Categories" &&
                    productSubCategories?.map(option => (
                        <>
                            <ListItem {...shopMyBranchDCLeftNavStyles.innerListItemStyle} key={option?.name}>
                                <Link href={option?.path}>{option?.shortDescription}</Link>
                            </ListItem>
                        </>
                    ))}
                {!session.isAuthenticated &&
                    title === "Brands" && [...byIdArray] 
                    .sort((a, b) => a.name.localeCompare(b.name)) 
                    .splice(0, 15) 
                    .map((item) => (
                      <ListItem {...shopMyBranchDCLeftNavStyles.innerListItemStyle} key={item.name}>
                        <Link href={"/catalog" + item.detailPagePath}>{item.name}</Link>
                      </ListItem>
                ))}
                {!session.isAuthenticated && title === "Brands" && showMoreButton && byIdArray?.length! > 0 && (
                    <ListItem
                        {...shopMyBranchDCLeftNavStyles.innerListItemStyle}
                        {...shopMyBranchDCLeftNavStyles.moreBrandsLinkStyle}
                    >
                        <Link href="/catalog/shop-brands">
                            <Img src="https://www.adiglobaldistribution.us/Themes/ADIGlobal/Images/icons/icon-more@3x.png"></Img>
                            More
                        </Link>
                    </ListItem>
                )}
                {session.isAuthenticated && title === "Brands" && showMoreButton && leftNavData?.length! > 0 && (
                    <ListItem
                        {...shopMyBranchDCLeftNavStyles.innerListItemStyle}
                        {...shopMyBranchDCLeftNavStyles.moreBrandsLinkStyle}
                    >
                        <Link href="/catalog/shop-brands">
                            <Img src="https://www.adiglobaldistribution.us/Themes/ADIGlobal/Images/icons/icon-more@3x.png"></Img>
                            More
                        </Link>
                    </ListItem>
                )}
            </UnorderedList>
        </ListItem>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ADILeftNav);